import React, { useEffect, useRef, useState } from "react";
import ImageComponent from "../ui/ImageComponent";

interface HeaderPostProps {
  title: string;
  banner: { path?: string } | string;
  description?: string;
  playUrl?: string;
  onPlay?: (url?: string) => void;
  type: "movie" | "comic" | "novel";
  firstChapterId?: string;
  lastChapterId?: string;
  loading?: boolean;
}

const HeaderPost: React.FC<HeaderPostProps> = ({
  title,
  banner,
  description,
  playUrl,
  onPlay,
  type,
  firstChapterId,
  lastChapterId,
  loading = false,
}) => {
  const descRef = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [showToggle, setShowToggle] = useState(false);
  const bannerPath = typeof banner === "string" ? banner : banner?.path || "";

  // Handle description overflow detection
  useEffect(() => {
    if (type === "movie") {
      const checkOverflow = () => {
        if (!descRef.current) return;
        descRef.current.classList.add("line-clamp-4", "overflow-hidden");
        descRef.current.classList.remove("overflow-auto");
        descRef.current.style.maxHeight = "";
        const clientH = descRef.current.clientHeight;
        const scrollH = descRef.current.scrollHeight;
        setShowToggle(scrollH > clientH);
      };
      checkOverflow();
      window.addEventListener("resize", checkOverflow);
      return () => window.removeEventListener("resize", checkOverflow);
    }
  }, [description, type]);

  // Toggle description expand/collapse
  useEffect(() => {
    if (type === "movie" && descRef.current) {
      if (expanded) {
        descRef.current.classList.remove("line-clamp-4", "overflow-hidden");
        descRef.current.classList.add("overflow-y-auto");
        descRef.current.style.maxHeight = "120px"; // Set fixed height for scrolling
      } else {
        descRef.current.classList.add("line-clamp-4", "overflow-hidden");
        descRef.current.classList.remove("overflow-y-auto");
        descRef.current.style.maxHeight = "";
      }
    }
  }, [expanded, type]);

  // Render movie play button
  const renderMoviePlayButton = () => {
    if (type !== "movie") return null;

    if (!playUrl) {
      return (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center z-20">
          <span className="bg-gray-700 text-white px-4 py-2 rounded-full opacity-80 text-lg font-semibold">
            Không có video
          </span>
        </div>
      );
    }

    return (
      <div className="action-play absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex gap-4 items-center z-20">
        <div
          onClick={() => playUrl && onPlay?.(playUrl)}
          className="bg-primary flex gap-3 items-center px-4 py-2 rounded-full whitespace-nowrap cursor-pointer"
        >
          <svg
            width="16"
            height="22"
            viewBox="0 0 16 22"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15.2089 10.6335C15.7599 11.0429 15.7441 11.8729 15.178 12.261L1.56546 21.5936C0.901852 22.0485 5.35697e-07 21.5734 5.16316e-07 20.7688L4.79044e-08 1.32229C2.81054e-08 0.500314 0.936598 0.0293746 1.59639 0.51959L15.2089 10.6335Z"
              fill="white"
            />
          </svg>
          <span className="text-white text-lg font-semibold">Play</span>
        </div>
      </div>
    );
  };

  // Render comic/novel controls
  const renderComicNovelControls = () => {
    if (type !== "comic" && type !== "novel") return null;

    // Show loading spinner
    if (loading) {
      return (
        <div className="absolute left-1/2 top-[60%] z-10 flex -translate-x-1/2 -translate-y-1/2">
          <svg
            className="animate-spin h-8 w-8 text-primary"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            ></path>
          </svg>
        </div>
      );
    }

    // Show "Đang phát hành" if no chapters available
    if (!firstChapterId && !lastChapterId) {
      return (
        <div className="absolute left-1/2 top-[60%] z-10 flex -translate-x-1/2 -translate-y-1/2">
          <span className="bg-gray-700 text-white px-4 py-3 md:px-6 md:py-4 rounded font-semibold shadow opacity-80 text-sm md:text-lg">
            Đang phát hành
          </span>
        </div>
      );
    }

    // Show chapter buttons
    return (
      <div className="absolute left-1/2 top-[60%] z-10 w-full justify-center flex -translate-x-1/2 -translate-y-1/2 gap-2 md:gap-4">
        {firstChapterId && (
          <button
            className={`bg-primary px-3 py-2 md:px-6 md:py-4 rounded-full text-white font-semibold shadow hover:bg-primary-dark transition text-xs md:text-base ${
              firstChapterId ? "" : "opacity-50 cursor-not-allowed"
            }`}
            onClick={() => firstChapterId && onPlay?.(firstChapterId)}
            disabled={!firstChapterId}
          >
            <div className="flex items-center justify-center gap-1 md:gap-2">
              <svg
                width="12"
                height="14"
                viewBox="0 0 16 22"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-3 h-3 md:w-4 md:h-4"
              >
                <path
                  d="M15.2089 10.6335C15.7599 11.0429 15.7441 11.8729 15.178 12.261L1.56546 21.5936C0.901852 22.0485 5.35697e-07 21.5734 5.16316e-07 20.7688L4.79044e-08 1.32229C2.81054e-08 0.500314 0.936598 0.0293746 1.59639 0.51959L15.2089 10.6335Z"
                  fill="white"
                />
              </svg>
              <span>Đọc truyện</span>
            </div>
          </button>
        )}
        {lastChapterId && (
          <button
            className={`bg-[#6B87F9] px-3 py-1 md:px-6 md:py-2 rounded-full text-white font-semibold hover:bg-secondary-dark transition text-xs md:text-base ${
              lastChapterId ? "" : "opacity-50 cursor-not-allowed"
            }`}
            onClick={() => lastChapterId && onPlay?.(lastChapterId)}
            disabled={!lastChapterId}
          >
            <div className="flex items-center justify-center gap-1 md:gap-2">
              <svg
                width="24"
                height="24"
                viewBox="0 0 50 50"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 md:w-5 md:h-5"
              >
                <path
                  d="M43.5884 31.7603L39.0579 24.8644L43.5421 18.2498C43.6486 18.0922 43.7067 17.9119 43.7105 17.7274C43.7143 17.5429 43.6636 17.3608 43.5637 17.1997C43.4637 17.0387 43.3181 16.9044 43.1417 16.8107C42.9653 16.717 42.7645 16.6673 42.5598 16.6665H6.47807C5.86504 16.6665 5.27711 16.886 4.84363 17.2767C4.41015 17.6674 4.16663 18.1973 4.16663 18.7498L4.16663 31.2498C4.16663 31.8024 4.41015 32.3323 4.84363 32.723C5.27711 33.1137 5.86504 33.3332 6.47807 33.3332H42.5944C42.7984 33.3331 42.9988 33.2844 43.1751 33.192C43.3515 33.0996 43.4975 32.9668 43.5985 32.807C43.6995 32.6473 43.7517 32.4663 43.7499 32.2824C43.7481 32.0986 43.6924 31.9184 43.5884 31.7603ZM16.3133 28.3123H15.0189L11.2512 23.7915V28.3228H9.94524V21.8748H11.2512L15.0304 26.4061V21.8748H16.3248L16.3133 28.3123ZM23.629 22.9165H19.5493V24.4686H23.2476V25.5103H19.5493V27.1873H23.629V28.229H18.2433V21.8748H23.6175L23.629 22.9165ZM33.21 28.2915H31.904L30.1126 23.5103L28.3213 28.3123H27.0268L24.7154 21.8748H26.1254L27.6509 26.4894L29.4076 21.8748H30.8176L32.505 26.4894L34.0421 21.8748H35.4636L33.21 28.2915Z"
                  fill="white"
                />
              </svg>

              <span>Chương mới</span>
            </div>
          </button>
        )}
      </div>
    );
  };

  // Render title with correct positioning
  const renderTitle = () => {
    const titleClassName =
      "w-full title absolute text-2xl md:text-4xl text-center font-semibold leading-8 line-clamp-2 px-4 text-white";
    const titleStyle = { textShadow: "0 2px 8px rgba(0,0,0,0.7)" };

    // Different positioning for movie vs comic/novel
    const positionClass =
      type === "movie"
        ? "top-8 left-1/2 -translate-x-1/2 -translate-y-1/2"
        : "bottom-8 left-1/2 -translate-x-1/2";

    return (
      <h1 className={`${titleClassName} ${positionClass}`} style={titleStyle}>
        {title}
      </h1>
    );
  };

  // Render movie description
  const renderMovieDescription = () => {
    if (type !== "movie") return null;

    return (
      <div className="z-10 description-inBanner w-full md:w-3/4 absolute bottom-0 left-1/2 -translate-x-1/2 h-auto py-2 md:py-4">
        <div className="content text-sm text-grey-80 px-2 text-center">
          <p
            ref={descRef}
            className="description-text-inBanner text-xs md:text-lg transition-all duration-300 w-full mx-auto line-clamp-4 overflow-hidden max-h-20 md:max-h-40 text-white scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-transparent"
            style={{
              textShadow: "0 2px 8px rgba(0,0,0,0.7)",
              scrollbarWidth: "thin",
            }}
          >
            {description}
          </p>
        </div>
        <div className="action text-center mt-1 md:mt-2">
          {showToggle && (
            <span
              className="readmore-toggle-inBanner text-primary hover:text-primary-dark text-xs md:text-sm cursor-pointer select-none transition"
              role="button"
              aria-expanded={expanded}
              onClick={() => setExpanded((prev) => !prev)}
            >
              <span className={expanded ? "hidden" : "text-more-inBanner"}>
                Xem thêm
              </span>
              <span className={expanded ? "text-compact-inBanner" : "hidden"}>
                Thu gọn
              </span>
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="featured-img relative">
      <div
        className="relative w-full responsive-banner rounded overflow-hidden aspect-video mb-2 md:mb-4"
        style={{
          height: "auto",
          ...(typeof window !== "undefined" && window.innerWidth < 768
            ? { height: "70vh", minHeight: "240px", maxHeight: "100vw" }
            : {}),
        }}
      >
        <ImageComponent
          src={bannerPath}
          alt={title}
          className="w-full h-full object-cover"
        />
        <div
          className="absolute inset-0 pointer-events-none z-0 bottom-0"
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            zIndex: 0,
            bottom: 0,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-end",
            justifyContent: "center",
            backgroundImage: "linear-gradient(180deg, #fff0 0%, #010101 60%)",
            transition: "max-height 0.5s ease, opacity 0.5s ease",
            overflow: "hidden",
            height: "fit-content",
            opacity: 1,
          }}
        />

        {renderMoviePlayButton()}
        {renderComicNovelControls()}
        {renderTitle()}
        {renderMovieDescription()}
      </div>
    </div>
  );
};

export default HeaderPost;
